import { useCallback, useRef, useState } from "react";
import axios from "axios";
import { useLanguage } from "@/context/LanguageContext";
import { trackEvent } from "@/utils/ga4";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialFormState = {
  name: "",
  phone: "",
  email: "",
  location: "",
  landSize: "",
  waterSource: "",
  message: "",
};

const logClientError = (message, error) => {
  if (process.env.NODE_ENV !== "production") {
    console.error(message, error);
  }
};

const buildContactPayload = (formData, token) => ({
  ...formData,
  turnstile_token: token,
});

const submitContactLead = (payload) => axios.post(`${API}/contact/submit`, payload);

export const useContactForm = () => {
  const { copy } = useLanguage();
  const [formData, setFormData] = useState(initialFormState);
  const [status, setStatus] = useState("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileRef = useRef(null);

  const resetTurnstile = useCallback(() => {
    if (turnstileRef.current) {
      turnstileRef.current.reset();
    }
  }, [turnstileRef]);

  const handleChange = useCallback(
    (event) => {
      const { name, value } = event.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
    },
    [setFormData],
  );

  const handleTurnstileVerify = useCallback(
    (token) => {
      setTurnstileToken(token);
      setErrorMessage("");
    },
    [setErrorMessage, setTurnstileToken],
  );

  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken("");
  }, [setTurnstileToken]);

  const handleFormError = useCallback(
    (message) => {
      setStatus("error");
      setErrorMessage(message);
      setTurnstileToken("");
      resetTurnstile();
    },
    [resetTurnstile, setErrorMessage, setStatus, setTurnstileToken],
  );

  const handleFormSuccess = useCallback(() => {
    setStatus("success");
    setFormData(initialFormState);
    setTurnstileToken("");
    resetTurnstile();
  }, [resetTurnstile, setFormData, setStatus, setTurnstileToken]);

  const handleTurnstileError = useCallback(() => {
    handleFormError(copy.contact.formCaptchaError);
  }, [copy.contact.formCaptchaError, handleFormError]);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      setErrorMessage("");

      if (!turnstileToken) {
        handleFormError(copy.contact.formCaptchaRequired);
        return;
      }

      setStatus("sending");

      try {
        const payload = buildContactPayload(formData, turnstileToken);
        await submitContactLead(payload);
        trackEvent("lead_submit", { form: "contact" });
        handleFormSuccess();
      } catch (error) {
        logClientError("Failed to submit contact form", error);
        handleFormError(copy.contact.formError);
      }
    },
    [
      copy.contact.formCaptchaRequired,
      copy.contact.formError,
      formData,
      handleFormError,
      handleFormSuccess,
      buildContactPayload,
      submitContactLead,
      setErrorMessage,
      setStatus,
      trackEvent,
      turnstileToken,
    ],
  );

  return {
    formData,
    status,
    errorMessage,
    handleChange,
    handleSubmit,
    turnstileRef,
    handleTurnstileVerify,
    handleTurnstileExpire,
    handleTurnstileError,
  };
};
